import { getS3ObjectURL } from '@shared/utils/helpers/s3';
import Image from 'next/image';

type Size = 'xs' | 'sm' | 'md' | 'lg';

const SIZES: Record<Size, number> = {
  xs: 16,
  sm: 36,
  md: 56,
  lg: 75
};

type Props = {
  imageKey: string | null | undefined;
  alt: string;
  // Named sizes for product/store surfaces; a number for exact-px contexts
  // (award badges, tag-modal rows) that need to line up with neighbors.
  size?: Size | number;
  round?: boolean;
};

// The one component for product/tron/award image rendering across the app.
// TRON products and awards use round=true for a circle that matches user
// avatars; other products render as-is.
const ProductImage = ({ imageKey, alt, size = 'md', round = false }: Props) => {
  const px = typeof size === 'number' ? size : SIZES[size];

  if (!imageKey) {
    return (
      <div
        className={`shrink-0 bg-background-secondary ${round ? 'rounded-full' : ''}`}
        style={{ width: px, height: px }}
      />
    );
  }

  const src = imageKey.startsWith('http') ? imageKey : getS3ObjectURL(imageKey);

  return (
    <div
      className={`relative shrink-0 overflow-hidden ${round ? 'rounded-full' : ''}`}
      style={{ width: px, height: px }}
    >
      <Image
        src={src || '/images/display-image-default.png'}
        alt={alt}
        fill
        // The box is a fixed px size, so the rendered width is exactly that —
        // tells Next which srcset candidate to pick and silences the dev warning.
        sizes={`${px}px`}
        className="object-contain"
      />
    </div>
  );
};

export default ProductImage;
